import React, { useState, useEffect } from 'react';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { Painting } from '../models/Painting';
import { Box, Stack } from '@mui/material';


export default function FocusedPainting({painting, textSize = 1}: {painting: Painting, textSize?: number}) {
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    // Reset to try full image first whenever painting changes
    setImageSrc(painting.image.replace('.png', '-Full.png'));
  }, [painting.image]);
  
  const imageWidth = { xs: '70vw', sm: '50vw', md: '22vw' };

  const handleImageError = () => {
    // Fallback to original image if full version doesn't exist
    setImageSrc(painting.image);
  };

  return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          width: { xs: '95vw', sm: '90vw', md: '85vw', lg: '80vw' },
          maxWidth: '100%',
          gap: { xs: 1, md: 2 },
          padding: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: 'fit-content',
            height: 'fit-content',
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            flexShrink: 0
          }}
        >
          <Box sx={{
            // Simple container without frame
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
            width: imageWidth,
            maxWidth: '100%'
          }}>
            {/* Container for the painting with minimal white space */}
            <Box sx={{
              width: '100%', // Full width to minimize white space
              height: '100%', // Full height to minimize white space
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CardMedia
                  component="img"
                  image={imageSrc}
                  alt={`${painting.name} ${painting.description}`}
                  onError={handleImageError}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain', // Preserve aspect ratio without cropping
                    display: 'block',
                    border: '2px solid white' // Small white border
                  }}
              />
            </Box>
          </Box>
        </Box>
        <Stack sx={{
          flex: 1,
          minWidth: 0, // Allow shrinking
          maxWidth: '100%',
          padding: { xs: 1, md: 2 },
          gap: { xs: 0.5, md: 1 }
        }}>
            <Typography
              align="left"
              fontFamily={"Grenze"}
              fontStyle={"italic"}
              fontSize={`${Math.min(3 * textSize, 4)}vw`}
              sx={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: 1.2,
                fontWeight: 'bold'
              }}
            >
                {painting.name}
            </Typography>
            <Typography
              align="left"
              fontFamily={"Bitter"}
              fontSize={`${Math.min(2 * textSize, 2.5)}vw`}
              sx={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: 1.3,
                fontWeight: 'bold'
              }}
            >
                {painting.description}
            </Typography>
            <Typography
              align="left"
              fontFamily={"Bitter"}
              fontSize={`${Math.min(2 * textSize, 2.5)}vw`}
              sx={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: 1.3,
                fontWeight: 'bold'
              }}
            >
                {painting.date}
            </Typography>
            <Typography
              align="left"
              fontFamily={"Fraunces"}
              fontSize={`${Math.min(2 * textSize, 2.5)}vw`}
              sx={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 'normal'
              }}
            >
                {painting.longDescription}
            </Typography>
        </Stack>
      </Stack>
  );
}